import React from "react";
import ToothSelector from "./ToothSelector";
import { fieldShellClass, useFormValidation } from "./FormValidationContext";

type ToothFieldProps = {
  label: string;
  toothNumbers: string[];
  onToothClick: (tooth: number) => void;
  optional?: boolean;
  hint?: string;
  /** Override validation error state */
  error?: boolean;
};

const ToothField: React.FC<ToothFieldProps> = ({
  label,
  toothNumbers,
  onToothClick,
  optional = false,
  hint,
  error,
}) => {
  const { showErrors } = useFormValidation();
  const invalid =
    error ?? (showErrors && !optional && toothNumbers.length === 0);

  return (
    <div className={fieldShellClass(invalid)} data-invalid={invalid || undefined}>
      <div className="flex flex-col gap-1.5 md:flex-row md:items-start md:gap-4">
        <div className="shrink-0 md:w-[32%] md:pt-1">
          <label
            className={`text-xs font-medium ${
              invalid ? "text-[var(--cu-danger)]" : "text-[var(--cu-muted)]"
            }`}
          >
            {label}
            {!optional && <span className="ml-1 text-[var(--cu-danger)]">*</span>}
            {optional && (
              <span className="ml-1.5 text-[11px] font-normal text-[var(--cu-subtle)]">
                optional
              </span>
            )}
            {invalid && (
              <span className="ml-1.5 text-[11px] font-normal text-[var(--cu-danger)]">
                Required
              </span>
            )}
          </label>
          {hint && (
            <p className="mt-0.5 text-[11px] leading-snug text-[var(--cu-subtle)]">
              {hint}
            </p>
          )}
          {toothNumbers.length > 0 && (
            <p className="mt-1 text-[11px] text-[var(--cu-accent)]">
              {[...toothNumbers]
                .sort((a, b) => Number(a) - Number(b))
                .map((t) => `#${t}`)
                .join(", ")}
            </p>
          )}
          {invalid && (
            <p className="mt-1 text-[11px] text-[var(--cu-danger)]">
              Select at least one tooth
            </p>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <ToothSelector
            toothNumbers={toothNumbers}
            onToothClick={onToothClick}
          />
        </div>
      </div>
    </div>
  );
};

export default ToothField;
